/** Welches Datum beschriftet eine Stufe der Nachfass-Zeitleiste?
 *
 *  Warum es diese Datei gibt (Tester-Befund F-13, 03.08.2026): bei Intervall
 *  1 Tag ab Fr 31.07. zeigte die Leiste „01.08., 04.08." mit dem Haken auf
 *  01.08. — die Mail ging aber erst Mo 03.08. 09:08 raus. Die Kette las sich
 *  damit als 1.8.→4.8. (drei Tage) statt 3.8.→4.8. (ein Tag).
 *
 *  Falsch war NUR die Beschriftung. Der Fahrplan selbst rechnet richtig und
 *  bleibt unangetastet: `compute_steps` legt R1 auf Sa 1.8., `within_send_window`
 *  blockt nur den VERSAND (Mo–Fr), Montag feuert R1 nach und `reshift_remaining`
 *  re-ankert die Folgestufen. Das geplante Datum darf sich nicht verschieben —
 *  `app/mail/follow_up_schedule.py:150-154` begruendet das ausdruecklich: eine
 *  Holding-Zusage hat dem Kunden dieses Datum bereits genannt.
 *
 *  Also: eine GESENDETE Stufe traegt ihr tatsaechliches Sendedatum, das geplante
 *  Datum bleibt im Tooltip erhalten. Alles andere (geplant, uebersprungen,
 *  Altbestand ohne `sent_at`) beschriftet weiterhin das Faelligkeitsdatum.
 *
 *  Eigene Datei, damit die Auswahl ohne Browser pruefbar ist
 *  (`frontend/fahrplan-datum.test.mjs`, Muster: `calendarSlotTime.ts`).
 */

/** Feste Zeitzone fuer das SENDE-Datum.
 *
 *  `sent_at` ist ein Instant (UTC, `datetime` in `app/schemas/follow_up.py:161`).
 *  Ein Versand um 22:30 UTC ist in Berlin schon der naechste Tag — und der
 *  Nachfass-Motor denkt durchgehend in Berliner Geschaeftszeit (Sendefenster
 *  9–17 Uhr, `app/mail/follow_up_engine.py:206-210`). Deshalb wird der Instant
 *  hier explizit in Europe/Berlin ausgewiesen und NICHT in der Zone des
 *  Geraets: ein Reise-Laptop wuerde sonst einen anderen Tag anzeigen als der
 *  Fahrplan meint. (Dieselbe Begruendung wie `Termin.tsx:139-145`.)
 *
 *  Das FAELLIGKEITS-Datum braucht das nicht: `due_date` ist ein reiner
 *  Kalendertag (YYYY-MM-DD) ohne Zeitanteil. */
const SENT_TZ = "Europe/Berlin";

const SENT_FMT = new Intl.DateTimeFormat("de-DE", {
  timeZone: SENT_TZ,
  day: "numeric",
  month: "numeric",
});

/** Kleines tabellarisches Datum „4.3." unter jedem Fahrplan-Symbol. */
export function fmtDayMonth(d: Date | null): string {
  if (!d) return "–";
  return `${d.getDate()}.${d.getMonth() + 1}.`;
}

/** Instant → „3.8." in Berliner Zeit. `null` wenn nichts Brauchbares dasteht.
 *
 *  Aus `formatToParts` zusammengesetzt statt aus `format()`, damit die Form
 *  exakt die von `fmtDayMonth` ist und nicht an den Locale-Daten der jeweiligen
 *  ICU-Version haengt.
 *
 *  Exportiert, seit die Nachfass-Zeile ihre Erklaerungen zeigt („abwartend seit
 *  19.8.", „Zusage aus der Antwort vom 19.8."). Die Zeitstempel dahinter sind
 *  dieselbe Sorte Instant wie `sent_at`, und sie muessen aus demselben Grund in
 *  Berliner Zeit stehen: der Nachfass-Motor denkt durchgehend in Berliner
 *  Geschaeftszeit. Eine zweite Fassung dieser sechs Zeilen waere genau die
 *  Stelle, an der eine Zeile 19.8. sagt und die daneben 20.8. */
export function berlinDayMonth(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const parts = SENT_FMT.formatToParts(d);
  const day = parts.find((p) => p.type === "day")?.value;
  const month = parts.find((p) => p.type === "month")?.value;
  if (!day || !month) return null;
  return `${day}.${month}.`;
}

export interface FahrplanStep {
  /** Faelligkeitsdatum der Stufe (aus `due_date`). */
  due: Date | null;
  status?: "scheduled" | "sent" | "skipped";
  /** `sent_at` der Stufe — bei Altbestand und ungesendeten Stufen `null`. */
  sentAt?: string | null;
}

export interface FahrplanStepDate {
  /** Was unter dem Symbol steht. */
  label: string;
  /** Tooltip der Stufe. */
  title: string;
}

export function fahrplanStepDate(step: FahrplanStep): FahrplanStepDate {
  const geplant = fmtDayMonth(step.due);
  // Der STATUS entscheidet, nicht die blosse Anwesenheit von `sent_at`.
  const gesendet = step.status === "sent" ? berlinDayMonth(step.sentAt) : null;
  // Altbestand ohne `sent_at`: lieber das geplante Datum als ein „Invalid Date".
  if (!gesendet) return { label: geplant, title: `geplant ${geplant}` };
  return { label: gesendet, title: `geplant ${geplant} · gesendet ${gesendet}` };
}
